import React from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import { DeltaPill } from '../../components/primitives/DeltaPill';
import { StreamingTileModel } from '../streamingTotals';
import { sourceLook } from '../streamingSources';
import { formatChangePct, formatCompact, formatDay } from '../streamingFormat';
import { streamingTotalLabels, streamingTotalsMessages as messages } from '../streamingTotalsMessages';
import { SourceMark } from './SourceMark';
import { Panel } from '../../components/primitives/Panel';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingTotalTileProps {
  tile: StreamingTileModel;
  /** True: Streams comes from the distributor alone ("Ditto · all stores"). */
  fromDistributor: boolean;
  /** True: only a distributor is connected, so a dash says what to connect (design f4). */
  distributorOnly: boolean;
  small?: boolean;
}

/**
 * One total: the number, the change against the period before, and the source line (design a1).
 * A number that no connected source gives is a dash with a footnote, never 0. A tile fed only by
 * a signed-out source keeps its last number and shows "Stopped {date}" in place of the change.
 */
const StreamingTotalTileComponent: React.FC<StreamingTotalTileProps> = ({
  tile,
  fromDistributor,
  distributorOnly,
  small = false,
}) => {
  const intl = useIntl();
  const { current, previous, changePct } = tile.total;
  const label = intl.formatMessage(streamingTotalLabels[tile.key]);
  const change = formatChangePct(intl, changePct);
  const isMissing = current === null;
  const [firstSource] = tile.sources;

  const from =
    fromDistributor && tile.key === 'streams' && firstSource !== undefined
      ? intl.formatMessage(messages.fromDistributor, { source: sourceLook(firstSource).shortName })
      : intl.formatMessage(messages.fromSources, {
          sources: tile.sources.map((source) => sourceLook(source).shortName).join(', '),
        });

  return (
    <Panel className={clsx(styles.tile, small && styles.tileSmall)} role="group" aria-label={label}>
      <span className={styles.tileLabel}>{label}</span>
      <span className={clsx(styles.tileValue, isMissing && styles.tileValueFaint)}>{formatCompact(intl, current)}</span>
      {!isMissing ? (
        <span className={styles.tileRow}>
          {tile.stoppedAt !== null ? (
            <span className={styles.tileStopped}>
              {intl.formatMessage(messages.stopped, { date: formatDay(intl, tile.stoppedAt) })}
            </span>
          ) : null}
          {tile.stoppedAt === null && change !== null && changePct !== null ? (
            <DeltaPill value={changePct} label={change} />
          ) : null}
          {tile.stoppedAt === null && previous !== null && !small ? (
            <span className={styles.faint}>
              {intl.formatMessage(messages.vsPrevious, { previous: formatCompact(intl, previous) })}
            </span>
          ) : null}
        </span>
      ) : null}
      {!isMissing ? (
        <span className={styles.tileFrom}>
          {tile.sources.map((source) => (
            <SourceMark key={source} integrationType={source} />
          ))}
          <span>{from}</span>
        </span>
      ) : (
        <p className={styles.footnote}>
          {intl.formatMessage(
            distributorOnly && tile.key === 'listeners' ? messages.dittoNoListeners : messages.notGiven
          )}
        </p>
      )}
      {tile.filtered && !small ? <p className={styles.footnote}>{intl.formatMessage(messages.filtered)}</p> : null}
    </Panel>
  );
};

export const StreamingTotalTile = TypedMemo(StreamingTotalTileComponent);
